import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  useCustomerPortalParticipants,
  useCustomerPortalEligibleParticipants,
  useAddCustomerPortalParticipant,
  useRemoveCustomerPortalParticipant,
  isCompleteEmailAddress,
} from "../../hooks/useCustomerPortalParticipants";
import { usePortalBranding } from "../../hooks/usePortalBranding";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";
import {
  Users,
  UserPlus,
  User,
  X,
  Loader2,
  Search,
  Mail,
  Check,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import type { ParticipantResponse, EligibleParticipantResponse } from "../../types/tickets";

interface CustomerPortalParticipantsProps {
  ticketId: string;
  isTicketOwner?: boolean;
}

export function CustomerPortalParticipants({ ticketId, isTicketOwner = false }: CustomerPortalParticipantsProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState<EligibleParticipantResponse[]>([]);
  const [isAdding, setIsAdding] = useState(false);

  const { getThemedPrimaryColor, getThemedSecondaryColor, getContrastText, getThemedPrimaryWithOpacity } = usePortalBranding();
  const { data: participants = [], isLoading } = useCustomerPortalParticipants(ticketId);
  const { data: eligibleParticipants = [], isLoading: isSearching } = useCustomerPortalEligibleParticipants(
    ticketId,
    searchQuery
  );
  const addParticipantMutation = useAddCustomerPortalParticipant();
  const removeParticipantMutation = useRemoveCustomerPortalParticipant();

  const handleToggleSelection = (customer: EligibleParticipantResponse) => {
    setSelectedCustomers(prev => {
      const isSelected = prev.some(c => c.id === customer.id);
      if (isSelected) {
        return prev.filter(c => c.id !== customer.id);
      } else {
        return [...prev, customer];
      }
    });
  };

  const handleAddSelectedParticipants = async () => {
    if (selectedCustomers.length === 0) return;

    setIsAdding(true);
    // Tracks who was being added when a failure stops the loop; everyone before them was added
    let current: EligibleParticipantResponse | undefined;
    try {
      // Add all selected participants
      for (const customer of selectedCustomers) {
        current = customer;
        await addParticipantMutation.mutateAsync({
          ticketId,
          customerId: customer.id,
        });
      }

      const count = selectedCustomers.length;
      showToast.success(
        "Participants Added",
        count === 1
          ? `${selectedCustomers[0].fullName} has been added to this ticket`
          : `${count} participants have been added to this ticket`
      );
      setSearchQuery("");
      setSelectedCustomers([]);
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error("Failed to add participants:", error);
      const reason = getErrorMessage(error, "Failed to add participant");
      showToast.error("Error", current ? `${current.fullName}: ${reason}` : reason);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsAddDialogOpen(open);
    if (!open) {
      // Clear selection when dialog closes
      setSelectedCustomers([]);
      setSearchQuery("");
    }
  };

  const handleRemoveParticipant = async (participant: ParticipantResponse) => {
    try {
      await removeParticipantMutation.mutateAsync({
        ticketId,
        customerId: participant.customerId,
      });
      showToast.success("Participant Removed", `${participant.fullName} has been removed from this ticket`);
    } catch (error) {
      console.error("Failed to remove participant:", error);
      showToast.error("Error", getErrorMessage(error, "Failed to remove participant"));
    }
  };

  return (
    <Card className="shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
      <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            Participants
            {participants.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({participants.length})
              </span>
            )}
          </CardTitle>
          {isTicketOwner && (
            <Dialog open={isAddDialogOpen} onOpenChange={handleDialogOpenChange}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 transition-all duration-200"
                  style={{
                    borderColor: getThemedSecondaryColor(),
                    color: getThemedSecondaryColor(),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                    e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = getThemedSecondaryColor();
                  }}
                >
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Add</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Participant</DialogTitle>
                  <DialogDescription>
                    Add someone to this ticket. They will receive ticket notifications.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  {selectedCustomers.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {selectedCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                          style={{
                            backgroundColor: getThemedPrimaryWithOpacity(0.15),
                            color: getThemedPrimaryColor(),
                          }}
                        >
                          <span className="truncate max-w-[120px]">{customer.fullName}</span>
                          <button
                            onClick={() => handleToggleSelection(customer)}
                            className="rounded-full p-0.5 hover:opacity-70"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      inputMode="email"
                      placeholder="Enter their full email address"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {isSearching ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : eligibleParticipants.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {isCompleteEmailAddress(searchQuery)
                          ? "No contact found with that email address"
                          : "Enter the full email address of the person you want to add"}
                      </p>
                    ) : (
                      eligibleParticipants.map((customer) => {
                        const isSelected = selectedCustomers.some(c => c.id === customer.id);
                        return (
                          <button
                            key={customer.id}
                            onClick={() => handleToggleSelection(customer)}
                            className="w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left border"
                            style={{
                              backgroundColor: isSelected ? getThemedPrimaryWithOpacity(0.1) : 'transparent',
                              borderColor: isSelected ? getThemedPrimaryColor() : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.03)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{
                                backgroundColor: isSelected ? getThemedPrimaryColor() : getThemedPrimaryWithOpacity(0.1),
                              }}
                            >
                              {isSelected ? (
                                <Check className="h-4 w-4" style={{ color: getContrastText(getThemedPrimaryColor()) }} />
                              ) : (
                                <User className="h-4 w-4" style={{ color: getThemedPrimaryColor() }} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {customer.fullName}
                              </p>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                <span className="truncate">{customer.email}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {selectedCustomers.length > 0 && (
                    <div className="pt-4 border-t">
                      <Button
                        onClick={handleAddSelectedParticipants}
                        disabled={isAdding}
                        className="w-full transition-all duration-300 hover:scale-105 font-semibold shadow-lg hover:shadow-xl"
                        style={{
                          backgroundColor: getThemedPrimaryColor(),
                          color: getContrastText(getThemedPrimaryColor()),
                          borderColor: getThemedPrimaryColor(),
                          boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`,
                        }}
                        onMouseEnter={(e) => {
                          if (!isAdding) {
                            e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                            e.currentTarget.style.borderColor = getThemedSecondaryColor();
                            e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                            e.currentTarget.style.boxShadow = `0 25px 50px -12px ${getThemedPrimaryWithOpacity(0.5)}`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                          e.currentTarget.style.borderColor = getThemedPrimaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                          e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`;
                        }}
                      >
                        {isAdding ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Add {selectedCustomers.length} Participant{selectedCustomers.length !== 1 ? "s" : ""}
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : participants.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No participants added yet
          </p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {participants.map((participant) => (
              <div
                key={participant.id}
                className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: getThemedPrimaryWithOpacity(0.1) }}
                >
                  <User className="h-4 w-4" style={{ color: getThemedPrimaryColor() }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {participant.fullName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {participant.email}
                  </p>
                </div>
                {isTicketOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveParticipant(participant)}
                    disabled={removeParticipantMutation.isPending}
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {removeParticipantMutation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <X className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
